import React, { useState, useMemo, useEffect, useRef } from 'react';
import Checkbox from './Checkbox';
import IconButton from './IconButton';
import { percentage as percentageFormatter, colour } from '../styles/GlobalStyles';
import { formatPhoneNumber } from '../functions';
import { Copy, Chevron } from '../assets/icons';
import { FaviconImg } from './SourceIcon';
import { hostFromUrl, faviconUrlForHost } from '../lib/sourceFaviconUtils';
import { rescoreDynamicCriteria, computePreferenceSummary } from '../utils/buyerMatchScoring';
import '../styles/BuyerMatchesSection.css';

// ---- Formatting helpers ----

const fmtCurrency = (val) => {
    if (val == null || isNaN(val)) return '-';
    const sign = val < 0 ? '-' : '';
    const abs = Math.abs(val);
    if (abs >= 1000000) return `${sign}$${(abs / 1000000).toFixed(1)}M`;
    if (abs >= 1000) return `${sign}$${Math.round(abs / 1000)}K`;
    return `${sign}$${Math.round(abs)}`;
};

const fmtPercent = (val) => {
    if (val == null || isNaN(val)) return '-';
    // All values are already in percentage form (e.g. 21.4 = 21.4%)
    return `${val.toFixed(1)}%`;
};

const fmtRehabLevels = (levels) => {
    if (!levels?.length) return '-';
    // Dedupe — incoming arrays often have ['turnkey', 'cosmetic', 'cosmetic']
    // which reads as noise. One representative per level is enough.
    const seen = new Set();
    const unique = [];
    for (const l of levels) {
        if (!l) continue;
        if (seen.has(l)) continue;
        seen.add(l);
        unique.push(String(l).replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()));
    }
    return unique.join(', ') || '-';
};

const fmtRehabLevel = (level) => {
    if (!level) return '-';
    return level.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
};

const PROP_TYPE_SHORT = {
    single_family: 'SFH', multi_family: 'Multi-Family', townhouse: 'Townhouse',
    condo: 'Condo', mobile: 'Mobile', commercial: 'Commercial', land: 'Land', portfolio: 'Portfolio'
};

const fmtPropertyTypes = (types) => {
    if (!types?.length) return '-';
    const seen = new Set();
    const unique = [];
    for (const t of types) {
        if (!t || seen.has(t)) continue;
        seen.add(t);
        unique.push(PROP_TYPE_SHORT[t] || t.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()));
    }
    return unique.join(', ') || '-';
};

const fmtPropertyType = (type) =>
    PROP_TYPE_SHORT[type] || (type ? type.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()) : '-');

// ---- Display generators per criterion ----

export const getCriterionDisplay = (key, entry) => {
    if (!entry?.buyerCriteria) return null;
    const bc = entry.buyerCriteria;
    const pv = entry.propertyValue;

    switch (key) {
        case 'geography': {
            // Portfolio spectrum display
            if (entry.matchType === 'portfolio_spectrum') {
                const parts = [];
                const zips = bc.zipCodesPreview || bc.zipCodes?.slice(0, 3) || [];
                const zipCount = bc.zipCodeCount ?? bc.zipCodes?.length ?? 0;
                if (zips.length) {
                    const zipStr = zips.join(', ');
                    parts.push(zipCount > 3 ? `ZIP ${zipStr} + ${zipCount - 3} more` : `ZIP ${zipStr}`);
                }
                if (bc.polygonNames?.length) parts.push(bc.polygonNames.join(', '));
                return {
                    wants: parts.join(' | ') || '-',
                    has: `${entry.matchedLeadCount} of ${entry.totalLeadCount} properties match (${entry.matchPercentage}%)`
                };
            }
            const parts = [];
            // Support both new (zipCodesPreview/zipCodeCount) and old (zipCodes) data shapes
            const zips = bc.zipCodesPreview || bc.zipCodes?.slice(0, 3) || [];
            const zipCount = bc.zipCodeCount ?? bc.zipCodes?.length ?? 0;
            if (zips.length) {
                const zipStr = zips.join(', ');
                parts.push(zipCount > 3 ? `ZIP ${zipStr} + ${zipCount - 3} more` : `ZIP ${zipStr}`);
            }
            if (bc.polygonNames?.length) parts.push(bc.polygonNames.join(', '));
            return { wants: parts.join(' | ') || '-', has: pv ? `ZIP ${pv}` : '-' };
        }
        case 'priceRange': {
            let wants;
            if (bc.min != null && bc.max != null) {
                wants = `${fmtCurrency(bc.min)} – ${fmtCurrency(bc.max)}`;
            } else if (bc.max != null) {
                wants = `Up to ${fmtCurrency(bc.max)}`;
            } else if (bc.min != null) {
                wants = `From ${fmtCurrency(bc.min)}`;
            } else {
                wants = '-';
            }
            return { wants, has: fmtCurrency(pv) };
        }
        case 'rehabLevel':
            return { wants: fmtRehabLevels(bc.levels), has: fmtRehabLevel(pv) };
        case 'propertyType':
            return { wants: fmtPropertyTypes(bc.types), has: fmtPropertyType(pv) };
        case 'characteristics': {
            const wantsParts = [];
            if (bc.minBedrooms != null || bc.maxBedrooms != null) {
                if (bc.minBedrooms != null && bc.maxBedrooms != null) wantsParts.push(`${bc.minBedrooms}-${bc.maxBedrooms} bd`);
                else if (bc.minBedrooms != null) wantsParts.push(`${bc.minBedrooms}+ bd`);
                else wantsParts.push(`up to ${bc.maxBedrooms} bd`);
            }
            if (bc.minBathrooms != null || bc.maxBathrooms != null) {
                if (bc.minBathrooms != null && bc.maxBathrooms != null) wantsParts.push(`${bc.minBathrooms}-${bc.maxBathrooms} ba`);
                else if (bc.minBathrooms != null) wantsParts.push(`${bc.minBathrooms}+ ba`);
                else wantsParts.push(`up to ${bc.maxBathrooms} ba`);
            }
            const hasParts = [];
            // Co-living deals are matched on the projected post-conversion room count, so
            // this can exceed the bed count on the listing — spell that out rather than
            // appearing to disagree with the lead header.
            if (pv?.bedrooms != null) {
                hasParts.push(
                    pv.listedBedrooms != null && pv.listedBedrooms !== pv.bedrooms
                        ? `${pv.bedrooms} bd projected (${pv.listedBedrooms} listed)`
                        : `${pv.bedrooms} bd`
                );
            }
            if (pv?.bathrooms != null) hasParts.push(`${pv.bathrooms} ba`);
            if (pv?.squareFeet != null) hasParts.push(`${pv.squareFeet.toLocaleString()} sf`);
            return { wants: wantsParts.join(', ') || '-', has: hasParts.join(', ') || '-' };
        }
        case 'dealStructure': {
            const wantsParts = [];
            if (bc.sellerFinancing) wantsParts.push('Seller Finance');
            if (bc.subTo) wantsParts.push('Subject-To');
            if (typeof bc.maxInterestRate === 'number') wantsParts.push(`≤${bc.maxInterestRate}% rate`);
            if (typeof bc.maxDownPayment === 'number') wantsParts.push(`≤$${bc.maxDownPayment.toLocaleString()} down`);
            const hasParts = [];
            if (pv?.sellerFinance) hasParts.push('Seller Finance');
            if (pv?.subTo) hasParts.push('Subject-To');
            const ft = pv?.financingTerms;
            if (ft) {
                if (typeof ft.interestRate === 'number') hasParts.push(`${ft.interestRate}% APR`);
                if (typeof ft.loanBalance === 'number') hasParts.push(`$${ft.loanBalance.toLocaleString()} balance`);
                if (typeof ft.monthlyPayment === 'number') hasParts.push(`$${ft.monthlyPayment.toLocaleString()}/mo`);
                if (typeof ft.downPayment === 'number') hasParts.push(`$${ft.downPayment.toLocaleString()} down`);
            }
            return { wants: wantsParts.join(', ') || '-', has: hasParts.length ? hasParts.join(', ') : 'None' };
        }
        case 'amenities': {
            const wantsParts = [];
            if (bc.requiresPool) wantsParts.push('Pool');
            if (bc.requiresGarage) {
                wantsParts.push(bc.minGarageSpaces ? `Garage (${bc.minGarageSpaces}+)` : 'Garage');
            }
            const hasParts = [];
            if (pv?.pool) hasParts.push('Pool');
            if (pv?.garage) {
                hasParts.push(pv.garageSpaces ? `Garage (${pv.garageSpaces})` : 'Garage');
            }
            return { wants: wantsParts.join(', ') || '-', has: hasParts.length ? hasParts.join(', ') : 'None' };
        }
        case 'floodZone':
            return { wants: 'Accepts flood zone', has: (Array.isArray(pv) ? pv.join(', ') : pv) || '-' };
        case 'profitReturns': {
            // Co-living returns ONLY. Flip profitReturns is rendered separately (via the
            // details/threshold rows below), so return null for the flip shape to keep
            // that path unchanged. The co-living breakdown tags propertyValue with
            // coLiving-prefixed keys.
            if (!pv || !('coLivingCapRate' in pv)) return null;
            const wantsParts = [];
            if (bc.minCapRate != null) wantsParts.push(`${bc.minCapRate}%+ cap`);
            if (bc.minNetCashFlow != null) wantsParts.push(`${fmtCurrency(bc.minNetCashFlow)}+/mo`);
            if (bc.minCashOnCashReturn != null) wantsParts.push(`${bc.minCashOnCashReturn}%+ cash-on-cash`);
            if (bc.minRatePerRoom != null) wantsParts.push(`${fmtCurrency(bc.minRatePerRoom)}+/room`);
            const hasParts = [];
            if (pv.coLivingCapRate != null) hasParts.push(`${pv.coLivingCapRate}% cap`);
            if (pv.coLivingCashFlowMonthly != null) hasParts.push(`${fmtCurrency(pv.coLivingCashFlowMonthly)}/mo`);
            if (pv.coLivingCashOnCash != null) hasParts.push(`${pv.coLivingCashOnCash}% cash-on-cash`);
            if (pv.coLivingRatePerRoom != null) hasParts.push(`${fmtCurrency(pv.coLivingRatePerRoom)}/room`);
            return { wants: wantsParts.join(', ') || '-', has: hasParts.join(', ') || '-' };
        }
        default:
            return null;
    }
};

const PROFIT_THRESHOLD_LABELS = {
    minProfitDollars: 'Min Profit',
    minProfitPercentage: 'Min Profit %',
    maxPurchasePricePercentOfARV: 'Max % of ARV',
    minRoiPercent: 'Min ROI',
};

const fmtProfitValue = (criterion, value) => {
    if (value == null || isNaN(value)) return '-';
    if (criterion === 'minProfitDollars') return fmtCurrency(value);
    return fmtPercent(value);
};

// ---- Criterion order and fallback labels (when breakdown entry missing for old payloads) ----
export const CRITERION_ORDER = ['geography', 'priceRange', 'profitReturns', 'rehabLevel', 'propertyType', 'characteristics', 'amenities', 'dealStructure', 'floodZone'];
export const CRITERION_LABELS = {
    geography: 'Location',
    priceRange: 'Price Range',
    profitReturns: 'Profit & Returns',
    rehabLevel: 'Rehab Level',
    propertyType: 'Property Type',
    characteristics: 'Property Specs',
    amenities: 'Amenities',
    dealStructure: 'Deal Structure',
    floodZone: 'Flood Zone',
};

// Bulk-select tiers for the "Select" menu. minFrac null = every displayed
// match (the old "Select All"); the rest mirror the match-quality cutoffs.
const SELECT_TIERS = [
    { key: 'all', label: 'All matches', minFrac: null },
    { key: 'excellent', label: 'Excellent · 80%+', minFrac: 0.80 },
    { key: 'great', label: 'Great · 70%+', minFrac: 0.70 },
    { key: 'good', label: 'Good · 65%+', minFrac: 0.65 },
];

// ---- Component ----

function BuyerMatchesSection({
    matches,
    selectedBuyerIds = null,
    onToggleBuyer = null,
    onOpenBuyerProfile = null,
    propertyValues = null,
    profitResult = null,
    onCopyEmail = null,
    crmLinks = null,
    searchTerm = '',
    onReplaceSelection = null,
    totalMatched = null,
    rowsLoading = false,
}) {
    // NO score floor here — the backend alone decides who is a match, and this list
    // must agree with the "N buyers" chip on the leads table, which counts every entry
    // in data.buyerMatchScores. ⚠️ That map is NO LONGER 1:1 with `matches`: it is built
    // from the matcher's uncapped list, while `matches` stops at MAX_STORED_MATCHES (40).
    // So the chip and this header agree on the COUNT via the totalMatched prop, and the
    // rows below are the top slice we hold breakdowns for. The generic scorer
    // already drops sub-50% buyers before persisting; co-living specialist entries
    // deliberately bypass that floor (their gates are exact-ZIP + path viability, so an
    // expensive pair is meant to surface ranked down, WITH the number). Re-applying a
    // 50% floor here hid those entirely — a lead whose chip read "3 buyers" opened to
    // "Buyer Matches (0)". Sorted by % descending; manual buyers (0%) sort last.
    const sortedMatches = useMemo(
        () => [...matches].sort((a, b) => (b.percentage ?? 0) - (a.percentage ?? 0)),
        [matches],
    );

    // Dual-purpose search: the same input that finds unmatched buyers in the
    // parent dropdown also narrows the visible match cards here.
    const displayedMatches = useMemo(() => {
        const term = (searchTerm || '').toLowerCase().trim();
        if (!term) return sortedMatches;
        return sortedMatches.filter((m) => {
            const name = (m.buyerName || '').toLowerCase();
            const email = (m.buyerEmail || '').toLowerCase();
            const phone = (m.buyerPhone || '').toLowerCase();
            return name.includes(term) || email.includes(term) || phone.includes(term);
        });
    }, [sortedMatches, searchTerm]);

    const selectable = selectedBuyerIds !== null && onToggleBuyer !== null;
    const [expandedBuyers, setExpandedBuyers] = useState(new Set());
    // Per-buyer toggle for "Show N inactive criteria" — keeps inactives hidden
    // by default so the breakdown shows only what the buyer actually configured.
    const [showInactiveFor, setShowInactiveFor] = useState(new Set());

    const toggleShowInactive = (buyerId, e) => {
        if (e) e.stopPropagation();
        setShowInactiveFor(prev => {
            const next = new Set(prev);
            if (next.has(buyerId)) next.delete(buyerId);
            else next.add(buyerId);
            return next;
        });
    };

    const toggleExpanded = (buyerId, e) => {
        e.stopPropagation();
        setExpandedBuyers(prev => {
            const next = new Set(prev);
            if (next.has(buyerId)) next.delete(buyerId);
            else next.add(buyerId);
            return next;
        });
    };

    // Bulk select via one "Select" menu — consolidates the old Select All button
    // and the threshold slider into a single control (PatternFly/Gmail bulk-select
    // pattern): named tiers with live counts, empty tiers disabled so you never
    // land on a "0 buyers" dead end. Each pick REPLACES the selection. Scoped to
    // displayedMatches so an active search narrows what a tier selects; manual
    // buyers (0%) only fall under "All matches", never a % tier.
    const canBulkSelect = selectable && onReplaceSelection !== null;
    const [selectMenuOpen, setSelectMenuOpen] = useState(false);
    const selectMenuRef = useRef(null);

    const tierCounts = useMemo(() => {
        const counts = {};
        for (const tier of SELECT_TIERS) {
            counts[tier.key] = tier.minFrac === null
                ? displayedMatches.length
                : displayedMatches.reduce((n, m) => n + ((m.percentage ?? 0) >= tier.minFrac ? 1 : 0), 0);
        }
        return counts;
    }, [displayedMatches]);

    // Close the menu on outside click / Escape, only while it's open.
    useEffect(() => {
        if (!selectMenuOpen) return undefined;
        const onDown = (e) => {
            if (selectMenuRef.current && !selectMenuRef.current.contains(e.target)) setSelectMenuOpen(false);
        };
        const onKey = (e) => { if (e.key === 'Escape') setSelectMenuOpen(false); };
        document.addEventListener('mousedown', onDown);
        document.addEventListener('keydown', onKey);
        return () => {
            document.removeEventListener('mousedown', onDown);
            document.removeEventListener('keydown', onKey);
        };
    }, [selectMenuOpen]);

    const selectTier = (tier) => {
        if (!onReplaceSelection) return;
        const ids = displayedMatches
            .filter((m) => tier.minFrac === null || (m.percentage ?? 0) >= tier.minFrac)
            .map((m) => m.buyerId);
        onReplaceSelection(ids);
        setSelectMenuOpen(false);
    };

    const clearSelection = () => {
        if (!onReplaceSelection) return;
        onReplaceSelection([]);
        setSelectMenuOpen(false);
    };

    // Re-score dynamic criteria when propertyValues change
    const rescoredMap = useMemo(() => {
        if (!propertyValues) return null;
        const map = new Map();
        for (const match of sortedMatches) {
            // Only re-score if breakdown has enriched format (active field exists)
            if (match.breakdown?.geography?.active !== undefined) {
                const updated = rescoreDynamicCriteria(match.breakdown, {
                    purchasePrice: propertyValues.purchasePrice,
                    arv: propertyValues.arv,
                    repairCost: propertyValues.repairCost,
                    squareFeet: propertyValues.squareFeet,
                    profitResult: profitResult?.hasAllData ? profitResult : null,
                });
                map.set(match.buyerId, {
                    breakdown: updated,
                    summary: computePreferenceSummary(updated),
                });
            }
        }
        return map;
    }, [sortedMatches, propertyValues, profitResult]);

    const selectedCount = selectable && selectedBuyerIds ? selectedBuyerIds.size : 0;
    // 🚨 The count must be the number of buyers who MATCHED, never the number of rows
    // we can render. `matches` is capped at MAX_STORED_MATCHES (40) — the matcher stores
    // only that many criteria breakdowns to keep the lead doc under Firestore's 1 MiB
    // ceiling — while data.buyerMatchScores (and therefore the leads-table chip, and the
    // auto-send recipient list) carries every one. Reading the array length here printed
    // "Buyer Matches (40)" on a deal matched to 195 buyers that we emailed all 195 of.
    const totalCount = typeof totalMatched === 'number' && totalMatched > sortedMatches.length
        ? totalMatched
        : sortedMatches.length;
    const visibleCount = displayedMatches.length;
    const hasSearch = (searchTerm || '').trim().length > 0;
    // Rows we hold breakdowns for, below the true total — say so rather than let the
    // list silently read as the whole roster.
    const listedCount = sortedMatches.length;
    const hasUnlisted = totalCount > listedCount;
    // Rows the page can still add (LeadBuyerMatches rebuilds the buyers we hold no
    // breakdown for once its roster fetch lands). While that's outstanding, a bulk
    // "Select" would silently take the subset that happens to be rendered — the exact
    // 40-of-195 undercount this list was fixed to stop — so hold the menu until the
    // rows we KNOW are missing have arrived. Nothing missing ⇒ nothing to wait for.
    const rowsStillArriving = rowsLoading && hasUnlisted;
    const headerCountLabel = hasSearch
        ? `Buyer Matches · ${visibleCount} of ${listedCount}`
        : `Buyer Matches (${totalCount})`;

    return (
        <div className="lead-detail-section">
            <div className="bms-list-shell">
                <div className="bms-list-header">
                    <div className="bms-list-header-counts">
                        <h3 className="heading-small-regular colour-primary lead-detail-section-title">
                            {headerCountLabel}
                        </h3>
                        {selectedCount > 0 && (
                            <span className="bms-list-selection-chip body-semibold colour-primary">
                                {selectedCount} selected
                            </span>
                        )}
                        {hasUnlisted && !hasSearch && (
                            <span className="body-regular colour-secondary">
                                {rowsStillArriving ? 'loading remaining buyers…' : `showing top ${listedCount}`}
                            </span>
                        )}
                    </div>
                    {canBulkSelect && displayedMatches.length > 0 && (
                        <div className="bms-select-menu-wrap" ref={selectMenuRef}>
                            <button
                                type="button"
                                className={`bms-select-trigger label-bold ${rowsStillArriving ? 'colour-tertiary cursor-disabled' : 'colour-primary cursor-pointer'}`}
                                onClick={rowsStillArriving ? undefined : () => setSelectMenuOpen((o) => !o)}
                                disabled={rowsStillArriving}
                                aria-haspopup="menu"
                                aria-expanded={selectMenuOpen}
                            >
                                Select
                                <Chevron fill={colour.gray06} className={selectMenuOpen ? 'rotate-90-negative' : 'rotate-90'} />
                            </button>
                            {selectMenuOpen && (
                                <div className="bms-select-menu" role="menu">
                                    {SELECT_TIERS.map((tier) => {
                                        const count = tierCounts[tier.key];
                                        const disabled = count === 0;
                                        return (
                                            <button
                                                key={tier.key}
                                                type="button"
                                                role="menuitem"
                                                className={`bms-select-menu-item ${disabled ? 'bms-select-menu-item-disabled' : 'cursor-pointer'}`}
                                                onClick={disabled ? undefined : () => selectTier(tier)}
                                                disabled={disabled}
                                            >
                                                <span className={`body-regular ${disabled ? 'colour-tertiary' : 'colour-primary'}`}>
                                                    {tier.label}
                                                </span>
                                                <span className={`label-regular bms-select-menu-count ${disabled ? 'colour-tertiary' : 'colour-secondary'}`}>
                                                    {count}
                                                </span>
                                            </button>
                                        );
                                    })}
                                    <div className="bms-select-menu-divider" />
                                    <button
                                        type="button"
                                        role="menuitem"
                                        className={`bms-select-menu-item ${selectedCount === 0 ? 'bms-select-menu-item-disabled' : 'cursor-pointer'}`}
                                        onClick={selectedCount === 0 ? undefined : clearSelection}
                                        disabled={selectedCount === 0}
                                    >
                                        <span className={`body-regular ${selectedCount === 0 ? 'colour-tertiary' : 'colour-secondary'}`}>
                                            Clear selection
                                        </span>
                                    </button>
                                </div>
                            )}
                        </div>
                    )}
                </div>
                <div className="bms-list-scroll">
                {displayedMatches.length === 0 ? (
                    <div className="bms-list-empty">
                        <span className="body-regular colour-secondary">
                            {hasSearch
                                ? `No matches for "${searchTerm.trim()}"`
                                : 'No buyer matches.'}
                        </span>
                    </div>
                ) : (
                <div className="bms-list-body lead-detail-buyer-matches">
                {displayedMatches.map((match) => {
                    const quality = match.matchQuality || (match.percentage >= 0.80 ? 'excellent' : match.percentage >= 0.65 ? 'good' : match.percentage >= 0.50 ? 'fair' : 'poor');
                    const pillTierClass = quality === 'manual' ? 'bms-row-pill-manual'
                        : quality === 'excellent' ? 'bms-row-pill-excellent'
                        : quality === 'good' ? 'bms-row-pill-good'
                        : 'bms-row-pill-fair';
                    const displayScore = match._isManual ? 'Manual' : match.percentage !== undefined ? percentageFormatter.format(match.percentage) : `${match.matchScore}`;
                    const isSelected = selectable && selectedBuyerIds.has(match.buyerId);
                    const isExpanded = expandedBuyers.has(match.buyerId);
                    const canOpenBuyerProfile = !!onOpenBuyerProfile && !!match.buyerId;
                    const crmLink = crmLinks?.[match.buyerId] || null;

                    // Use re-scored breakdown if available, otherwise server breakdown
                    const rescored = rescoredMap?.get(match.buyerId);
                    const breakdown = rescored?.breakdown || match.breakdown;
                    const isEnriched = breakdown?.geography?.active !== undefined;
                    const summary = rescored?.summary || match.preferenceSummary;
                    const hasEnrichedBreakdown = isEnriched && summary && summary.total > 0;
                    const hasLegacyBreakdown = !isEnriched && !!match.breakdown;
                    const hasMatchedCriteria = !match.breakdown && Array.isArray(match.matchedCriteria) && match.matchedCriteria.length > 0;
                    const canExpand = hasEnrichedBreakdown || hasLegacyBreakdown || hasMatchedCriteria;

                    // Build the contact subline (email · phone · Source) with interpunct
                    // separators so the line is scannable instead of glued together.
                    const subParts = [];
                    if (match.buyerEmail) subParts.push(match.buyerEmail);
                    if (match.buyerPhone) subParts.push(formatPhoneNumber(match.buyerPhone));
                    if (match.buyerSource) subParts.push(`Source: ${match.buyerSource}`);

                    return (
                        <div
                            key={match.buyerId}
                            className={`bms-row ${isSelected ? 'bms-row-selected' : ''} ${selectable ? 'bms-row-selectable' : ''} ${isExpanded ? 'bms-row-expanded' : ''}`}
                            onClick={selectable ? () => onToggleBuyer(match.buyerId) : undefined}
                        >
                            <div className="bms-row-main">
                                {selectable ? (
                                    <div className="bms-row-checkbox" onClick={(e) => e.stopPropagation()}>
                                        <Checkbox
                                            label={null}
                                            selected={isSelected}
                                            type="default"
                                            disabled={false}
                                            cta={() => onToggleBuyer(match.buyerId)}
                                        />
                                    </div>
                                ) : null}

                                {/* Content column sits beside the checkbox so name + email/phone
                                    share the same left edge automatically — no magic-number padding. */}
                                <div className="bms-row-body">
                                    <div className="bms-row-top">
                                        <span className="body-semibold colour-primary bms-row-name">
                                            {match.buyerName}
                                        </span>

                                        <span className={`bms-row-pill ${pillTierClass} label-bold`}>
                                            {displayScore}
                                        </span>

                                        {hasEnrichedBreakdown && (
                                            <span className={`bms-row-prefs label-regular ${summary.met === summary.total ? 'colour-green' : 'colour-secondary'}`}>
                                                {summary.met}/{summary.total} prefs
                                            </span>
                                        )}

                                        <span className="bms-row-spacer" />

                                        <div className="bms-row-actions" onClick={(e) => e.stopPropagation()}>
                                            {canOpenBuyerProfile && (
                                                <button
                                                    type="button"
                                                    className="bms-row-view-profile label-bold"
                                                    onClick={() => onOpenBuyerProfile(match.buyerId)}
                                                >
                                                    View Profile
                                                </button>
                                            )}
                                            {crmLink && (
                                                <IconButton
                                                    size="small"
                                                    icon={<FaviconImg src={faviconUrlForHost(hostFromUrl(crmLink.url))} alt="" size={14} />}
                                                    disabled={false}
                                                    clickFunc={() => {
                                                        window.open(crmLink.url, '_blank', 'noopener,noreferrer');
                                                    }}
                                                    standardIcon={false}
                                                    contextTooltip={true}
                                                    contextTooltipBG="dark"
                                                    contextTooltipText={crmLink.label}
                                                    customClass={null}
                                                    background={false}
                                                    bgType={null}
                                                />
                                            )}
                                            {onCopyEmail && (
                                                <IconButton
                                                    size="small"
                                                    icon={<Copy fill={colour.gray05} />}
                                                    disabled={false}
                                                    clickFunc={() => onCopyEmail(match.buyerId, match.buyerName)}
                                                    standardIcon={false}
                                                    contextTooltip={true}
                                                    contextTooltipBG="dark"
                                                    contextTooltipText="Email Body"
                                                    customClass={null}
                                                    background={false}
                                                    bgType={null}
                                                />
                                            )}
                                        </div>

                                        {canExpand && (
                                            <button
                                                type="button"
                                                className={`bms-row-chevron ${isExpanded ? 'bms-row-chevron-open' : ''}`}
                                                onClick={(e) => toggleExpanded(match.buyerId, e)}
                                                aria-label={isExpanded ? 'Hide breakdown' : 'Show breakdown'}
                                                aria-expanded={isExpanded}
                                            >
                                                <Chevron fill={colour.gray05} />
                                            </button>
                                        )}
                                    </div>

                                    {subParts.length > 0 && (
                                        <div className="bms-row-sub body-regular colour-secondary">
                                            {subParts.join(' · ')}
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* Expanded breakdown — enriched format */}
                            {isExpanded && hasEnrichedBreakdown && (
                                <div className="bms-row-breakdown bms-pref-grid" onClick={(e) => e.stopPropagation()}>
                                            {(() => {
                                                // Partition criteria into active + inactive. Active rows render by
                                                // default; inactives hide behind a toggle so the panel reflects what
                                                // the buyer actually configured.
                                                const activeKeys = [];
                                                const inactiveKeys = [];
                                                for (const k of CRITERION_ORDER) {
                                                    if (breakdown[k]?.active === true) activeKeys.push(k);
                                                    else inactiveKeys.push(k);
                                                }
                                                const inactiveShown = showInactiveFor.has(match.buyerId);
                                                const orderedKeys = inactiveShown
                                                    ? [...activeKeys, ...inactiveKeys]
                                                    : activeKeys;
                                                return (
                                                    <>
                                                    {orderedKeys.map((key) => {
                                                const entry = breakdown[key];
                                                const active = entry?.active === true;
                                                const label = entry?.label || CRITERION_LABELS[key] || key;

                                                if (!active) {
                                                    return (
                                                        <div key={key} className="bms-pref-row bms-pref-row-inactive">
                                                            <span className="bms-pref-label label-regular colour-tertiary">{label}</span>
                                                            <span className="bms-pref-wants-full label-regular colour-tertiary">Not set by buyer</span>
                                                        </div>
                                                    );
                                                }

                                                // Profit/Returns has its own rendering (sub-thresholds)
                                                if (key === 'profitReturns') {
                                                    const details = entry.details || [];
                                                    const activeDetails = details.filter(d => d.threshold != null);
                                                    const metCount = activeDetails.filter(d => d.met).length;

                                                    return (
                                                        <div key={key} className="bms-pref-group">
                                                            <div className="bms-pref-group-header">
                                                                <span className="body-semibold colour-primary bms-pref-group-label">
                                                                    {entry.label}
                                                                </span>
                                                                {activeDetails.length > 1 && (
                                                                    <span className="label-regular colour-secondary">
                                                                        {metCount}/{activeDetails.length} met
                                                                    </span>
                                                                )}
                                                                <span className={`bms-pref-icon ${entry.met ? 'colour-green' : 'colour-error'}`} aria-hidden="true">
                                                                    {entry.met ? '\u2713' : '\u2717'}
                                                                </span>
                                                            </div>
                                                            {activeDetails.map(d => (
                                                                <div key={d.criterion} className="bms-pref-row bms-pref-row-sub">
                                                                    <span className="bms-pref-label label-regular colour-secondary">
                                                                        {PROFIT_THRESHOLD_LABELS[d.criterion] || d.criterion}
                                                                    </span>
                                                                    <span className="bms-pref-wants label-regular colour-tertiary">
                                                                        {fmtProfitValue(d.criterion, d.threshold)}
                                                                    </span>
                                                                    <span className="bms-pref-arrow colour-tertiary" aria-hidden="true">&rarr;</span>
                                                                    <span className={`bms-pref-has body-semibold ${d.met ? 'colour-primary' : 'colour-error'}`}>
                                                                        {fmtProfitValue(d.criterion, d.actual)}
                                                                    </span>
                                                                    <span className={`bms-pref-icon ${d.met ? 'colour-green' : 'colour-error'}`} aria-hidden="true">
                                                                        {d.met ? '\u2713' : '\u2717'}
                                                                    </span>
                                                                </div>
                                                            ))}
                                                        </div>
                                                    );
                                                }

                                                const display = getCriterionDisplay(key, entry);
                                                if (!display) return null;

                                                return (
                                                    <div key={key} className="bms-pref-row">
                                                        <span className="bms-pref-label body-regular colour-primary">{entry.label}</span>
                                                        <span className="bms-pref-wants label-regular colour-tertiary">{display.wants}</span>
                                                        <span className="bms-pref-arrow colour-tertiary" aria-hidden="true">&rarr;</span>
                                                        <span className={`bms-pref-has body-semibold ${entry.met ? 'colour-primary' : 'colour-error'}`}>
                                                            {display.has}
                                                        </span>
                                                        <span className={`bms-pref-icon ${entry.met ? 'colour-green' : 'colour-error'}`} aria-hidden="true">
                                                            {entry.met ? '\u2713' : '\u2717'}
                                                        </span>
                                                    </div>
                                                );
                                            })}
                                                    {inactiveKeys.length > 0 && (
                                                        <button
                                                            type="button"
                                                            className="bms-pref-inactive-toggle label-bold"
                                                            onClick={(e) => toggleShowInactive(match.buyerId, e)}
                                                        >
                                                            {inactiveShown
                                                                ? `Hide ${inactiveKeys.length} inactive ${inactiveKeys.length === 1 ? 'criterion' : 'criteria'}`
                                                                : `Show ${inactiveKeys.length} inactive ${inactiveKeys.length === 1 ? 'criterion' : 'criteria'}`}
                                                        </button>
                                                    )}
                                                    </>
                                                );
                                            })()}
                                </div>
                            )}

                            {/* Expanded breakdown — legacy badge tags */}
                            {isExpanded && hasLegacyBreakdown && (
                                <div className="bms-row-breakdown lead-detail-buyer-match-criteria" onClick={(e) => e.stopPropagation()}>
                                    {match.breakdown.geography?.score > 0 && (
                                        <span className="lead-detail-criteria-tag label-regular colour-secondary">
                                            {match.breakdown.geography.matchType === 'polygon' ? '\uD83D\uDCCD Zone' : '\uD83D\uDCCD Zip'}
                                        </span>
                                    )}
                                    {match.breakdown.priceRange?.score >= 15 && (
                                        <span className="lead-detail-criteria-tag label-regular colour-secondary">{'\uD83D\uDCB0'} Price</span>
                                    )}
                                    {match.breakdown.profitReturns?.score >= 15 && (
                                        <span className="lead-detail-criteria-tag label-regular colour-secondary">{'\uD83D\uDCC8'} Profit</span>
                                    )}
                                    {match.breakdown.rehabLevel?.score >= 7 && (
                                        <span className="lead-detail-criteria-tag label-regular colour-secondary">{'\uD83D\uDD27'} Rehab</span>
                                    )}
                                    {match.breakdown.propertyType?.score >= 6 && (
                                        <span className="lead-detail-criteria-tag label-regular colour-secondary">{'\uD83C\uDFE0'} Type</span>
                                    )}
                                    {match.breakdown.characteristics?.score >= 6 && (
                                        <span className="lead-detail-criteria-tag label-regular colour-secondary">{'\uD83D\uDCCB'} Specs</span>
                                    )}
                                    {match.breakdown.dealStructure?.score >= 2 && (
                                        <span className="lead-detail-criteria-tag label-regular colour-secondary">{'\uD83E\uDD1D'} Deal</span>
                                    )}
                                </div>
                            )}
                            {/* Expanded breakdown — matchedCriteria fallback */}
                            {isExpanded && hasMatchedCriteria && (
                                <div className="bms-row-breakdown lead-detail-buyer-match-criteria" onClick={(e) => e.stopPropagation()}>
                                    {match.matchedCriteria.map((criteria, idx) => (
                                        <span key={idx} className="lead-detail-criteria-tag label-regular colour-secondary">
                                            {criteria.criterion === 'geography' && criteria.matchType === 'zip_code' && '\uD83D\uDCCD Zip'}
                                            {criteria.criterion === 'geography' && criteria.matchType === 'county' && '\uD83D\uDCCD County'}
                                            {criteria.criterion === 'geography' && criteria.matchType === 'polygon' && '\uD83D\uDCCD Zone'}
                                            {criteria.criterion === 'propertyType' && '\uD83C\uDFE0 Type'}
                                            {criteria.criterion === 'priceRange' && '\uD83D\uDCB0 Price'}
                                            {criteria.criterion === 'profitThresholds' && '\uD83D\uDCC8 Profit'}
                                            {criteria.criterion === 'propertyCharacteristics' && '\uD83D\uDCCB Specs'}
                                            {criteria.criterion === 'rehabLevel' && '\uD83D\uDD27 Rehab'}
                                        </span>
                                    ))}
                                </div>
                            )}
                        </div>
                    );
                })}
                </div>
                )}
                </div>
            </div>
        </div>
    );
}

export default BuyerMatchesSection;
